import {
  BaseClient,
  BaseResponse,
  fromCustomClient,
  type GeoTIFF,
} from "geotiff";

import { COG_SOURCE_BLOCK_CACHE_SIZE, COG_SOURCE_BLOCK_SIZE } from "./cogOrtho";

class FetchResponse extends BaseResponse {
  private readonly response: Response;

  constructor(response: Response) {
    super();
    this.response = response;
  }

  override get status(): number {
    return this.response.status;
  }

  override getHeader(name: string): string | undefined {
    return this.response.headers.get(name) ?? undefined;
  }

  override getData(): Promise<ArrayBuffer> {
    return this.response.arrayBuffer();
  }
}

/**
 * geotiff's built-in fetch client does not expose a cache mode. Range requests
 * against presigned S3 URLs can fail in Chrome's HTTP cache with
 * ERR_CACHE_OPERATION_NOT_SUPPORTED, and the cache is of little use here: the
 * URL signature rotates every ~50 minutes and geotiff keeps its own block cache.
 */
class NoStoreFetchClient extends BaseClient {
  override async request({
    headers,
    signal,
  }: RequestInit = {}): Promise<FetchResponse> {
    const response = await fetch(this.url, {
      cache: "no-store",
      headers,
      signal,
    });
    return new FetchResponse(response);
  }
}

/** Opens a COG over HTTP range requests, bypassing the browser HTTP cache. */
export const fromCogUrl = (url: string): Promise<GeoTIFF> =>
  fromCustomClient(new NoStoreFetchClient(url), {
    blockSize: COG_SOURCE_BLOCK_SIZE,
    cacheSize: COG_SOURCE_BLOCK_CACHE_SIZE,
  } as Parameters<typeof fromCustomClient>[1]);
